import { Locale } from '@headless-workspace/config';
import { ImageWithSrc } from '@headless-workspace/typings';

const numberOfKeys = (value: object): number => {
    return Object.keys(value).length;
};

const isEmpty = (value?: object): boolean => {
    return !value || numberOfKeys(value) === 0;
};

const createImageWithSrc = (src: string, alt = ''): ImageWithSrc => {
    return {
        src,
        alt,
    };
};

type GenericObject = {
    [key: string]: unknown;
};
const hasAttribute = (obj: GenericObject, attr: string): boolean => {
    return attr in obj && !!obj[attr];
};

const mapObject = <T, U>(
    object: Record<string, T>,
    mapFn: (value: T, locale?: Locale) => U,
    locale?: Locale,
): Record<string, U> => {
    return Object.keys(object).reduce(
        (acc, key) => {
            acc[key] = mapFn(object[key], locale);
            return acc;
        },
        {} as Record<string, U>,
    );
};

const isEnumValue = <T extends Record<string, string>>(value: string, enumObj: T): value is T[keyof T] => {
    return Object.values(enumObj).includes(value as T[keyof T]);
};

const isNotNullishGuard = <T>(value: T | null | undefined): value is T => {
    return Boolean(value);
};

const flatObjectArray = <T extends Record<string, unknown> | undefined>(object: T[]): T => {
    return object.reduce<T>((acc, current) => {
        return { ...acc, ...current };
    }, {} as T);
};

export function nestObject(flat: Record<string, string>): Record<string, string | Record<string, unknown>> {
    const nested: Record<string, string | Record<string, unknown>> = {};

    Object.keys(flat).forEach((key) => {
        const parts = key.split('.');
        let current: Record<string, string | Record<string, unknown>> = nested;

        parts.forEach((part, i) => {
            const isLast = i === parts.length - 1;

            if (isLast) {
                current[part] = flat[key];
            } else {
                if (typeof current[part] !== 'object' || current[part] === null) {
                    current[part] = {};
                }
                current = current[part] as Record<string, string | Record<string, unknown>>;
            }
        });
    });

    return nested;
}

const deepMerge = <T>(target: T, source: Partial<T>): T => {
    for (const key in source) {
        const sourceVal = source[key];
        const targetVal = target[key];

        if (
            typeof sourceVal === 'object' &&
            sourceVal !== null &&
            typeof targetVal === 'object' &&
            targetVal !== null &&
            !Array.isArray(sourceVal) &&
            !Array.isArray(targetVal)
        ) {
            target[key] = deepMerge({ ...targetVal }, sourceVal);
        } else {
            target[key] = sourceVal as T[Extract<keyof T, string>];
        }
    }

    return target;
};

export const ObjectHelpers = {
    numberOfKeys,
    isEmpty,
    createImageWithSrc,
    mapObject,
    isEnumValue,
    isNotNullishGuard,
    flatObjectArray,
    hasAttribute,
    nestObject,
    deepMerge,
};
